import { Paper } from '@yoast/ui-library';
import React from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import PaperHeader from '../../components/PaperHeader';

// Add a Step type and type the steps array
type Step = {
  path: string;
  id: number;
  title: string;
  description: string;
};

const steps: Step[] = [
  {
    path: '/setup',
    id: 1,
    title: 'Brand & location info',
    description:
      'Enter a few basic details about your brand so we can generate relevant queries people might use in AI platforms like ChatGPT and Perplexity. This helps us analyze how visible your brand is in their responses.',
  },
  {
    path: '/setup/:brandId/queries',
    id: 2,
    title: 'Queries',
    description:
      "Review the queries we've generated from your brand info. These are phrases people might type into AI platforms that could trigger your brand to appear.",
  },
  {
    path: '/setup/:brandId/analysis',
    id: 3,
    title: 'Analysis',
    description:
      "We're checking how your brand shows up in AI. Your queries will now be tested in ChatGPT, Perplexity and Gemini. This process should take around 5 minutes.",
  },
];

// Replace direct equality check with react-router matchPath so patterns like
// '/setup/:brandId/queries' will match '/setup/123/queries'
const getCurrentStep = (locationPathname: string): Step =>
  steps.find((step) => !!matchPath(step.path, locationPathname)) || steps[0];

export default function Setup() {
  const location = useLocation();

  const currentStep = getCurrentStep(location.pathname);

  return (
    <Paper className="yst-mx-auto yst-max-w-2xl">
      {/* TODO: move to a wrapping route around the sub-routes for /setup */}
      <PaperHeader
        title={<SetupPaperTitle currentStep={currentStep} steps={steps} />}
        description={currentStep.description}
      />
      <Paper.Content>
        <Outlet />
      </Paper.Content>
    </Paper>
  );
}

// Converted function into a typed React component
export const SetupPaperTitle: React.FC<{
  title?: string;
  currentStep: Step | number;
  steps: Step[] | number;
}> = ({ title, currentStep, steps }) => (
  <span className="yst-inline-flex yst-flex-row yst-gap-4">
    {/* letter space 20% */}
    <span className="yst-tracking-[.2em] yst-text-slate-400">
      <span className="yst-text-slate-900">
        {typeof currentStep === 'object' ? currentStep.id : currentStep}
      </span>
      /{Array.isArray(steps) ? steps.length : steps}
    </span>
    <span>
      {typeof currentStep === 'object'
        ? currentStep.title
        : title || `Step ${currentStep}`}
    </span>
  </span>
);
